import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import clsx from "clsx";
import {
  IAccordionFields,
  SpecificLocaleFields,
} from "~/@types/generated/contentful";
import { getComponentFromContentfulRichTextEntry } from "~/contentful";
import {
  InsightComponentData,
  useComponentData,
} from "~/contexts/ComponentDataProvider";

type AccordionBlock = {
  fields: SpecificLocaleFields<IAccordionFields>;
};

export default function Accordion({ fields }: AccordionBlock): JSX.Element {
  const { insight } = useComponentData() as InsightComponentData;
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: fields.accordionItems?.map((item) => ({
      "@type": "Question",
      name: item.fields.title,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.fields.body
          ? documentToPlainTextString(item.fields.body)
          : "",
      },
    })),
  };

  return (
    <section
      data-content-type="accordionBlock"
      data-module-title={fields.moduleTitle}
      className="col-span-full mt-20 mb-20 lg:col-span-8 lg:col-start-2 lg:mt-28"
    >
      {fields.accordionItems && fields.faQs && (
        <script
          type="application/ld+json"
          suppressHydrationWarning
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData),
          }}
        />
      )}
      <div className="tbi-grid" style={{ padding: insight ? "0" : "" }}>
        {fields.eyebrow && (
          <span className="eyebrows col-span-2 col-start-1 mb-16 lg:mb-0">
            {fields.eyebrow}
          </span>
        )}
        <div
          className={clsx(
            "col-span-full",
            insight
              ? "col-span-9 lg:col-start-4"
              : "lg:col-span-8 lg:col-start-3",
          )}
        >
          {fields.accordionItems?.map((item) => (
            <details
              key={item.sys.id}
              className='border-b-[1px] border-black pb-6 pt-6 first:pt-0 lg:pb-10 lg:pt-[50px] [&_p]:mt-6 lg:[&_p]:mt-10 [&_.twic-i]:mt-6 lg:[&_.twic-i]:mt-10 [&_summary]:open:after:content-["-"] [&_.global-container]:mt-0 [&_.global-container]:p-0'
            >
              <summary className='relative flex cursor-pointer list-none place-content-between items-center after:absolute after:right-0 after:text-24 after:font-extralight after:content-["+"]'>
                <h3 className="mr-6">{item.fields.title}</h3>
              </summary>
              {item.fields.body &&
                documentToReactComponents(
                  item.fields.body,
                  getComponentFromContentfulRichTextEntry,
                )}
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
